import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { mapQbToTransaction } from "../lib/qbSync";

// Manages the QuickBooks sync lifecycle: knowing whether QB is connected,
// kicking off the OAuth flow, and pulling transactions on demand. Mirrors
// the GHL sync hook but for outbound card charges from QB.
//
// On re-sync, rows that already exist (matched by externalId) are UPDATED
// with the latest name from QB (in case the previous sync had a worse
// fallback description). Truly new rows are added.
export function useQbSync({ existingTransactions = [], onAddTransactions, onUpdateTransaction } = {}) {
  const [connected, setConnected] = useState(null); // null = unknown, false = not connected, true = connected
  const [syncing, setSyncing] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [error, setError] = useState(null);
  const [lastResult, setLastResult] = useState(null); // { added, skipped }

  // Probe whether QB is connected by pinging the transactions endpoint
  // with a tiny since=today window (just to read connection state cheaply).
  const probe = useCallback(async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const jwt = sessionData?.session?.access_token;
      if (!jwt) { setConnected(false); return; }
      const today = new Date().toISOString().slice(0, 10);
      const res = await fetch(`/api/qb-transactions?since=${today}`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      if (res.status === 404) { setConnected(false); return; }
      if (!res.ok) { setConnected(false); return; }
      const data = await res.json();
      setConnected(!!data.connected);
    } catch {
      setConnected(false);
    }
  }, []);

  useEffect(() => { probe(); }, [probe]);

  // Connect: get the user's session JWT and redirect to /api/qb-connect with
  // it as the state param so the callback can identify the user on return.
  const connect = useCallback(async () => {
    const { data: sessionData } = await supabase.auth.getSession();
    const jwt = sessionData?.session?.access_token;
    if (!jwt) { setError("Not signed in"); return; }
    window.location.href = `/api/qb-connect?state=${encodeURIComponent(jwt)}`;
  }, []);

  const sync = useCallback(async ({ since } = {}) => {
    setError(null);
    setSyncing(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const jwt = sessionData?.session?.access_token;
      if (!jwt) throw new Error("Not signed in");
      // Default = 90 days back if no explicit since date given
      let sinceISO = since;
      if (!sinceISO) {
        const d = new Date();
        d.setDate(d.getDate() - 90);
        sinceISO = d.toISOString().slice(0, 10);
      }
      const res = await fetch(`/api/qb-transactions?since=${sinceISO}`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text.slice(0, 200));
      }
      const data = await res.json();
      const mapped = (data.transactions || []).map(mapQbToTransaction);

      // Index existing rows by externalId for upsert-style sync.
      const existingByExtId = new Map();
      (existingTransactions || []).forEach((t) => {
        if (t.externalId) existingByExtId.set(t.externalId, t);
      });

      const fresh = [];
      let updated = 0;
      for (const row of mapped) {
        if (!row.externalId) continue;
        const existing = existingByExtId.get(row.externalId);
        if (!existing) {
          fresh.push(row);
        } else if (existing.name !== row.name && onUpdateTransaction) {
          // Re-sync refreshes the description field if QB now returns a
          // better one. Other user-modified fields (status, match, personal
          // tag) are preserved.
          onUpdateTransaction(existing.id, { name: row.name });
          updated += 1;
        }
      }

      if (fresh.length > 0 && onAddTransactions) {
        onAddTransactions(fresh);
      }
      setLastSyncedAt(data.fetchedAt || new Date().toISOString());
      setLastResult({ added: fresh.length, updated, skipped: mapped.length - fresh.length - updated });
      setConnected(true);
    } catch (e) {
      setError(e.message || "Sync failed");
    } finally {
      setSyncing(false);
    }
  }, [existingTransactions, onAddTransactions]);

  // After the OAuth round-trip Intuit will redirect back with
  // ?qb_connected=1 in the hash query. Re-probe so the UI updates.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash.includes("qb_connected=1")) {
      probe();
      // Clean the marker from the URL
      const cleanHash = window.location.hash.replace(/[?&]qb_connected=1/, "");
      window.history.replaceState(null, "", window.location.pathname + cleanHash);
    }
  }, [probe]);

  return { connected, syncing, lastSyncedAt, error, lastResult, connect, sync };
}